/**
 * Wishlist groups module — /pages/wishlist
 *
 * Owns every group mutation surface: the create dialog, the inline rename form
 * in the group header, the destructive delete dialog, and the add-to-group
 * picker opened from a product card.
 *
 * Depends on window.Wishlist (request layer) and window.WishlistPage (state,
 * rendering, announcements). Loaded directly after wishlist-page.js.
 */
(function () {
  'use strict';

  var uid = 0;

  var ICON_CLOSE =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

  /* -----------------------------------------------------------------------
     Shared accessors
     ----------------------------------------------------------------------- */

  function core() { return window.Wishlist || {}; }
  function page() { return window.WishlistPage || null; }

  function esc(value) {
    var p = page();
    if (p && p.escape) return p.escape(value);
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function strings() {
    var p = page();
    return p && p.strings ? p.strings() : (window.__wishlistGroupStrings || {});
  }

  function baseStrings() {
    var p = page();
    return p && p.baseStrings ? p.baseStrings() : (window.__wishlistStrings || {});
  }

  function messageForError(err) {
    var p = page();
    if (p && p.messageForError) return p.messageForError(err);
    return strings().error_generic || baseStrings().error || '';
  }

  /** Whether the API flagged a field — decides placement, never the wording */
  function hasFieldError(err, field) {
    var p = page();
    return !!(p && p.hasFieldError && p.hasFieldError(err, field));
  }

  function isStale(err) {
    return !!(err && err.status === 404);
  }

  function handleStaleList(listId) {
    var p = page();
    if (p && p.handleStaleList) p.handleStaleList(listId);
  }

  function maxName() {
    var p = page();
    return p && p.MAX_NAME ? p.MAX_NAME : 60;
  }

  function maxGroups() {
    var p = page();
    return p && p.MAX_GROUPS ? p.MAX_GROUPS : 20;
  }

  function announce(message) {
    var p = page();
    if (p && p.announce) p.announce(message);
  }

  function apiUrl(key, fallback) {
    var api = core().API || {};
    return api[key] || fallback;
  }

  function req(url, options) {
    if (!core().request) {
      return Promise.reject(new Error('Wishlist request layer unavailable'));
    }
    return core().request(url, options);
  }

  function restoreFocus(trigger) {
    if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
      trigger.focus();
      return;
    }
    var p = page();
    var root = p && p.getRoot ? p.getRoot() : null;
    var panel = root ? root.querySelector('[role="tabpanel"]') : null;
    if (panel && typeof panel.focus === 'function') panel.focus();
  }

  /* -----------------------------------------------------------------------
     Field + dialog primitives
     ----------------------------------------------------------------------- */

  function fieldMarkup(id, labelText, value, describedCounter) {
    var limit = maxName();
    var describedBy = id + '-error' + (describedCounter ? ' ' + id + '-counter' : '');
    return '<div class="wishlist-field">' +
        '<label class="wishlist-field__label" for="' + id + '">' + esc(labelText) + '</label>' +
        '<input class="wishlist-field__input" type="text" id="' + id + '" name="name"' +
          ' maxlength="' + limit + '" autocomplete="off" spellcheck="false"' +
          ' value="' + esc(value || '') + '"' +
          ' aria-describedby="' + describedBy + '">' +
        '<p class="wishlist-field__error" id="' + id + '-error" role="alert" hidden></p>' +
        (describedCounter
          ? '<p class="wishlist-field__counter" id="' + id + '-counter">' +
              '<span class="visually-hidden">' + esc(strings().name_counter_label || '') + '</span>' +
              '<span data-wl-count>' + String((value || '').length) + '</span>/' + limit +
            '</p>'
          : '') +
      '</div>';
  }

  function wireField(scope, id) {
    var input = scope.querySelector('#' + id);
    var counter = scope.querySelector('[data-wl-count]');
    if (input && counter) {
      input.addEventListener('input', function () {
        counter.textContent = String(input.value.length);
      });
    }
    return input;
  }

  function setFieldError(input, errorEl, message) {
    if (!errorEl) return;
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        const wrapper = input.closest('.wishlist-field');
        if (wrapper) wrapper.classList.add('wishlist-field--with-error');
      }
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
      if (input) {
        input.removeAttribute('aria-invalid');
        const wrap = input.closest('.wishlist-field');
        if (wrap) wrap.classList.remove('wishlist-field--with-error');
      }
    }
  }

  /**
   * Build a native <dialog>. opts:
   *   title, bodyHtml, submitLabel, submitClass, cancelLabel,
   *   closeOnBackdrop (bool), trigger (element)
   */
  function buildDialog(opts) {
    uid += 1;
    var id = 'wishlist-dialog-' + uid;
    var s = strings();

    var dialog = document.createElement('dialog');
    dialog.className = 'wishlist-dialog';
    dialog.id = id;
    dialog.setAttribute('aria-labelledby', id + '-title');

    dialog.innerHTML =
      '<form class="wishlist-dialog__form" novalidate>' +
        '<div class="wishlist-dialog__header">' +
          '<h2 class="wishlist-dialog__heading" id="' + id + '-title">' + esc(opts.title) + '</h2>' +
          '<button type="button" class="wishlist-dialog__close" data-wl-close' +
            ' aria-label="' + esc(s.close || '') + '" title="' + esc(s.close || '') + '">' + ICON_CLOSE + '</button>' +
        '</div>' +
        '<div class="wishlist-dialog__body">' + opts.bodyHtml + '</div>' +
        '<p class="wishlist-dialog__error" role="alert" hidden></p>' +
        '<div class="wishlist-dialog__footer">' +
          '<button type="button" class="wishlist-button wishlist-button--ghost" data-wl-cancel>' +
            esc(opts.cancelLabel || s.cancel || '') +
          '</button>' +
          '<button type="submit" class="wishlist-button ' + (opts.submitClass || 'wishlist-button--primary') + '" data-wl-submit>' +
            esc(opts.submitLabel) +
          '</button>' +
        '</div>' +
      '</form>';

    document.body.appendChild(dialog);

    var refs = {
      id: id,
      dialog: dialog,
      form: dialog.querySelector('.wishlist-dialog__form'),
      body: dialog.querySelector('.wishlist-dialog__body'),
      error: dialog.querySelector('.wishlist-dialog__error'),
      submit: dialog.querySelector('[data-wl-submit]'),
      cancel: dialog.querySelector('[data-wl-cancel]'),
      busy: false,
      suppressRestore: false,
    };

    refs.setError = function (message) {
      if (message) {
        refs.error.textContent = message;
        refs.error.hidden = false;
      } else {
        refs.error.textContent = '';
        refs.error.hidden = true;
      }
    };

    refs.setBusy = function (isBusy) {
      refs.busy = isBusy;
      dialog.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      refs.submit.disabled = isBusy;
      refs.submit.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      refs.cancel.disabled = isBusy;
    };

    /**
     * closeQuietly: skip focus restoration because the caller moves focus
     * itself (re-render, or opening a follow-up dialog).
     */
    refs.close = function (closeQuietly) {
      refs.suppressRestore = !!closeQuietly;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
    };

    dialog.addEventListener('close', function () {
      if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
      if (!refs.suppressRestore) restoreFocus(opts.trigger);
    });

    dialog.addEventListener('cancel', function (event) {
      // Never abandon a dialog mid-request
      if (refs.busy) event.preventDefault();
    });

    refs.cancel.addEventListener('click', function (event) {
      event.preventDefault();
      if (refs.busy) return;
      refs.close();
    });

    dialog.querySelector('[data-wl-close]').addEventListener('click', function (event) {
      event.preventDefault();
      if (refs.busy) return;
      refs.close();
    });

    if (opts.closeOnBackdrop) {
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog && !refs.busy) refs.close();
      });
    }

    refs.open = function () {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    };

    return refs;
  }

  /* -----------------------------------------------------------------------
     Create group
     ----------------------------------------------------------------------- */

  function openCreate(trigger) {
    var s = strings();
    var p = page();
    if (!p) return;

    var fieldId = 'wishlist-create-name-' + (uid + 1);
    var refs = buildDialog({
      title: s.create_title || '',
      bodyHtml: fieldMarkup(fieldId, s.name_label || '', '', true),
      submitLabel: s.create_submit || '',
      submitClass: 'wishlist-button--primary',
      closeOnBackdrop: true,
      trigger: trigger,
    });

    var input = wireField(refs.dialog, fieldId);
    var fieldError = refs.dialog.querySelector('.wishlist-field__error');

    if (input) {
      input.setAttribute('placeholder', s.name_placeholder || '');
      input.addEventListener('input', function () {
        setFieldError(input, fieldError, '');
        refs.setError('');
      });
    }

    refs.form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (refs.busy) return;

      var value = input ? input.value.trim() : '';
      setFieldError(input, fieldError, '');
      refs.setError('');

      if (!value) {
        setFieldError(input, fieldError, s.error_empty_name || '');
        if (input) input.focus();
        return;
      }
      if (value.length > maxName()) {
        setFieldError(input, fieldError, s.error_name_length || '');
        if (input) input.focus();
        return;
      }
      if (p.listCount() >= maxGroups()) {
        refs.setError(s.error_limit || '');
        return;
      }

      refs.setBusy(true);
      req(apiUrl('listCreate', '/apps/wishlist/lists/create'), {
        method: 'POST',
        data: { name: value },
      })
        .then(function (body) {
          var raw = body && (body.list || body.data || body);
          var list = p.normalizeList(raw) || { id: '', name: value };
          refs.setBusy(false);
          refs.close(true);
          if (list.id) {
            p.applyCreate(list);
            announce(s.created || '');
          } else {
            p.reload({ focus: 'tab' });
          }
        })
        .catch(function (err) {
          refs.setBusy(false);
          // The field bag decides where the message lands; the copy is always ours.
          if (err && err.status === 422 && hasFieldError(err, 'name')) {
            setFieldError(input, fieldError, messageForError(err));
            if (input) input.focus();
            return;
          }
          refs.setError(messageForError(err));
        });
    });

    refs.open();
    if (input) input.focus();
  }

  /* -----------------------------------------------------------------------
     Inline rename
     ----------------------------------------------------------------------- */

  function startRename(list, headerEl, trigger) {
    var s = strings();
    var p = page();
    if (!p || !list || !headerEl) return;

    uid += 1;
    var fieldId = 'wishlist-rename-name-' + uid;

    var form = document.createElement('form');
    form.className = 'wishlist-group__header wishlist-group__header--editing';
    form.setAttribute('novalidate', 'novalidate');
    form.innerHTML =
      fieldMarkup(fieldId, s.rename_label || s.name_label || '', list.name, true) +
      '<div class="wishlist-group__actions">' +
        '<button type="submit" class="wishlist-button wishlist-button--primary" data-wl-submit>' +
          esc(s.rename_submit || '') +
        '</button>' +
        '<button type="button" class="wishlist-button wishlist-button--ghost" data-wl-cancel>' +
          esc(s.cancel || '') +
        '</button>' +
      '</div>';

    headerEl.parentNode.replaceChild(form, headerEl);

    var input = wireField(form, fieldId);
    var fieldError = form.querySelector('.wishlist-field__error');
    var submitBtn = form.querySelector('[data-wl-submit]');
    var cancelBtn = form.querySelector('[data-wl-cancel]');
    var busy = false;

    function setBusy(isBusy) {
      busy = isBusy;
      form.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      submitBtn.disabled = isBusy;
      submitBtn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      cancelBtn.disabled = isBusy;
      if (input) input.disabled = isBusy;
    }

    function cancel() {
      if (busy) return;
      p.render('rename');
    }

    cancelBtn.addEventListener('click', function (event) {
      event.preventDefault();
      cancel();
    });

    if (input) {
      input.addEventListener('input', function () {
        setFieldError(input, fieldError, '');
      });
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          cancel();
        }
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (busy) return;

      var value = input ? input.value.trim() : '';
      setFieldError(input, fieldError, '');

      if (!value) {
        setFieldError(input, fieldError, s.error_empty_name || '');
        if (input) input.focus();
        return;
      }
      if (value.length > maxName()) {
        setFieldError(input, fieldError, s.error_name_length || '');
        if (input) input.focus();
        return;
      }
      if (value === list.name) {
        cancel();
        return;
      }

      var previous = list.name;
      setBusy(true);
      p.applyRename(list.id, value);

      req(apiUrl('listRename', '/apps/wishlist/lists/rename'), {
        method: 'POST',
        data: { list_id: list.id, name: value },
      })
        .then(function (body) {
          var raw = body && (body.list || body.data || null);
          var updated = raw ? p.normalizeList(raw) : null;
          p.applyRename(list.id, updated && updated.name ? updated.name : value);
          p.render('rename');
          announce(s.renamed || '');
        })
        .catch(function (err) {
          p.applyRename(list.id, previous);

          // The group vanished elsewhere: abandon the inline form and resync.
          if (isStale(err)) {
            handleStaleList(list.id);
            return;
          }

          setBusy(false);
          setFieldError(input, fieldError, messageForError(err));
          if (input) input.focus();
        });
    });

    if (input) {
      input.focus();
      input.select();
    } else if (trigger) {
      restoreFocus(trigger);
    }
  }

  /* -----------------------------------------------------------------------
     Delete group
     ----------------------------------------------------------------------- */

  function openDelete(list, trigger) {
    var s = strings();
    var p = page();
    if (!p || !list) return;

    var refs = buildDialog({
      title: s.delete_title || '',
      bodyHtml:
        '<p class="wishlist-dialog__text wishlist-dialog__text--name">' + esc(list.name) + '</p>' +
        '<p class="wishlist-dialog__text" data-wl-warning>' + esc(s.delete_warning || '') + '</p>',
      submitLabel: s.delete_confirm || '',
      submitClass: 'wishlist-button--danger',
      closeOnBackdrop: false,
      trigger: trigger,
    });

    // Describe the dialog by its consequence warning — not by the group name.
    var warning = refs.dialog.querySelector('[data-wl-warning]');
    if (warning) {
      warning.id = refs.id + '-warning';
      refs.dialog.setAttribute('aria-describedby', warning.id);
    }

    refs.form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (refs.busy) return;

      refs.setError('');
      refs.setBusy(true);

      req(apiUrl('listDelete', '/apps/wishlist/lists/delete'), {
        method: 'POST',
        data: { list_id: list.id },
      })
        .then(function () {
          refs.setBusy(false);
          refs.close(true);
          announce(s.deleted || '');
          p.applyDelete(list.id);
        })
        .catch(function (err) {
          refs.setBusy(false);
          if (err && err.status === 404) {
            refs.close(true);
            p.applyDelete(list.id);
            return;
          }
          refs.setError(messageForError(err));
        });
    });

    refs.open();
    refs.cancel.focus();
  }

  /* -----------------------------------------------------------------------
     Add-to-group picker
     ----------------------------------------------------------------------- */

  function pickerBody(productId) {
    var s = strings();
    var p = page();
    var lists = p.getLists();
    var memberships = p.membershipsFor(productId);

    if (!lists.length) {
      return '<div class="wishlist-picker">' +
          '<p class="wishlist-picker__hint">' + esc(s.picker_empty || '') + '</p>' +
          '<button type="button" class="wishlist-button wishlist-button--primary" data-wl-picker-create>' +
            esc(s.create || '') +
          '</button>' +
        '</div>';
    }

    var items = '';
    lists.forEach(function (list, index) {
      var isMember = memberships.indexOf(list.id) !== -1;
      items +=
        '<li class="wishlist-picker__item">' +
          '<label class="wishlist-picker__label"' + (isMember ? ' title="' + esc(s.picker_member || '') + '"' : '') + '>' +
            '<input type="checkbox" data-wl-picker-index="' + index + '"' +
              (isMember ? ' checked disabled' : '') + '>' +
            '<span>' + esc(list.name) + '</span>' +
          '</label>' +
        '</li>';
    });

    return '<div class="wishlist-picker">' +
        '<p class="wishlist-picker__hint">' + esc(s.picker_hint || '') + '</p>' +
        '<ul class="wishlist-picker__list">' + items + '</ul>' +
      '</div>';
  }

  function openPicker(productId, trigger) {
    var s = strings();
    var p = page();
    if (!p || !productId) return;

    var refs = buildDialog({
      title: s.picker_title || '',
      bodyHtml: pickerBody(productId),
      submitLabel: s.picker_done || '',
      submitClass: 'wishlist-button--primary',
      cancelLabel: s.cancel || '',
      closeOnBackdrop: true,
      trigger: trigger,
    });

    // The picker commits on each selection; the footer submit just closes it.
    refs.cancel.hidden = true;
    refs.form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (refs.busy) return;
      refs.close();
    });

    var createBtn = refs.dialog.querySelector('[data-wl-picker-create]');
    if (createBtn) {
      createBtn.addEventListener('click', function (event) {
        event.preventDefault();
        refs.close(true);
        openCreate(trigger);
      });
    }

    function pickerBoxes() {
      return Array.prototype.slice.call(refs.dialog.querySelectorAll('[data-wl-picker-index]'));
    }

    /** While one add is in flight, no other group may be selected */
    function lockBoxes() {
      pickerBoxes().forEach(function (other) { other.disabled = true; });
    }

    /** Re-enable everything that is not (or did not become) a membership */
    function releaseBoxes() {
      pickerBoxes().forEach(function (other) { other.disabled = !!other.checked; });
    }

    refs.dialog.addEventListener('change', function (event) {
      var box = event.target;
      if (!box || box.type !== 'checkbox' || !box.hasAttribute('data-wl-picker-index')) return;
      if (!box.checked) return;
      if (refs.busy) { box.checked = false; return; }

      var index = parseInt(box.getAttribute('data-wl-picker-index'), 10);
      var list = p.getLists()[index];
      if (!list) { box.checked = false; return; }

      refs.setError('');
      lockBoxes();
      box.setAttribute('aria-busy', 'true');
      refs.setBusy(true);

      req(apiUrl('add', '/apps/wishlist/add'), {
        method: 'POST',
        data: { product_id: productId, list_id: list.id },
      })
        .then(function () {
          box.checked = true;
          box.removeAttribute('aria-busy');
          var label = box.closest('.wishlist-picker__label');
          if (label) label.setAttribute('title', s.picker_member || '');
          refs.setBusy(false);
          releaseBoxes();
          p.addMembership(productId, list.id);
          announce(s.picker_added || '');
        })
        .catch(function (err) {
          box.checked = false;
          box.removeAttribute('aria-busy');
          refs.setBusy(false);

          // The group vanished elsewhere: close the picker and resync the page.
          if (isStale(err)) {
            refs.close(true);
            handleStaleList(list.id);
            return;
          }

          releaseBoxes();
          refs.setError(messageForError(err));
        });
    });

    refs.open();
    var firstEnabled = refs.dialog.querySelector('.wishlist-picker input:not([disabled])');
    if (firstEnabled) firstEnabled.focus();
    else if (createBtn) createBtn.focus();
    else refs.submit.focus();
  }

  window.WishlistGroups = {
    openCreate: openCreate,
    openDelete: openDelete,
    openPicker: openPicker,
    startRename: startRename,
  };
})();
